"use client";
import React, { Fragment, useMemo, FC } from "react";

import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import { Store } from "~/bff/types/Store";
import { StoreDetailsPageTranslations } from "~/bff/types/StoreDetailsPageTranslations";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { getFormatMedia } from "~/theme/utils";
import { Nullable } from "~/types/general.types";

import { StoreService } from "../store-service/component";

import { STORE_SERVICES_CLASSES } from "./constants";
import { StoreServicesContainer } from "./styled";

export interface StoreServicesProps {
  store: Nullable<Store> | undefined;
  translations: Nullable<StoreDetailsPageTranslations> | undefined;
  className?: string;
}

export const StoreServices: FC<StoreServicesProps> = ({
  store,
  translations,
  className,
}) => {
  const theme = useTheme();
  const isFlexStart = !useMediaQuery(getFormatMedia(theme).BREAKPOINT_DESKTOP);

  const isEvenNumberOfServices = useMemo(() => {
    const servicesLength = store?.services?.length || 0;
    return servicesLength === 1 || servicesLength % 2 === 0;
  }, [store]);

  const getJustifyContentValue = useMemo(() => {
    if (isFlexStart) {
      return "flex-start";
    }

    return isEvenNumberOfServices ? "flex-start" : "center";
  }, [isEvenNumberOfServices, isFlexStart]);

  if (!store?.services?.length) {
    return null;
  }

  return (
    <StoreServicesContainer
      className={className}
      {...getTestAutomationProps("store-services")}
    >
      <Grid container rowSpacing={[2, 4]}>
        {!isFlexStart && <Grid item xs={3} sm={3} md={3} lg={3} xl={3} />}
        <Grid item xs={6}>
          <Typography className={STORE_SERVICES_CLASSES.title} variant="h3">
            {translations?.storeServicesText}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container justifyContent={getJustifyContentValue}>
            {store?.services.map((service, index) => {
              const shouldAddSecondSpacer =
                index > 1 &&
                index % 2 === 0 &&
                isEvenNumberOfServices &&
                !isFlexStart;

              return (
                <Fragment key={index}>
                  {index % 2 === 0 && isEvenNumberOfServices && !isFlexStart && (
                    <Grid item md={2} lg={2} xl={2} />
                  )}
                  {shouldAddSecondSpacer && <Grid item md={2} lg={2} xl={2} />}
                  <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                    <StoreService
                      store={store}
                      service={service}
                      translations={translations}
                    />
                  </Grid>
                </Fragment>
              );
            })}
          </Grid>
        </Grid>
      </Grid>
    </StoreServicesContainer>
  );
};
